import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { toast } from "sonner";
import { RefreshCw, ShieldAlert, ExternalLink, History } from "lucide-react";

// Monitor Fiscal / Regulatório (Admin Master) — ver docs/MONITOR_FISCAL.md.
// Esta tela só EXIBE o que o backend detectou/classificou/registrou. Ela
// NUNCA altera código, XSD, feature flag ou a integração fiscal em si —
// só permite consultar fontes/alertas, ativar/desativar uma fonte já
// semeada (nunca cadastrar URL nova) e mudar o STATUS de um alerta
// (fluxo humano: detectar -> analisar -> desenvolver -> homologar -> ok).

const SEVERITIES = ["CRITICAL", "HIGH", "MEDIUM", "LOW", "INFO"];
const SEVERITY_LABEL = { CRITICAL: "Crítico", HIGH: "Alto", MEDIUM: "Médio", LOW: "Baixo", INFO: "Informativo" };
const SEVERITY_BADGE = {
  CRITICAL: "bg-red-100 text-red-800", HIGH: "bg-orange-100 text-orange-800",
  MEDIUM: "bg-amber-100 text-amber-800", LOW: "bg-slate-100 text-slate-700", INFO: "bg-sky-100 text-sky-800",
};
const STATUS_LABEL = {
  novo: "Novo", em_analise: "Em análise", requer_atualizacao: "Requer atualização",
  em_desenvolvimento: "Em desenvolvimento", homologado: "Homologado", resolvido: "Resolvido", ignorado: "Ignorado",
};
const STATUS_OPTIONS = Object.keys(STATUS_LABEL);
const SOURCE_STATUS_LABEL = {
  ok: "OK", requires_mtls: "Requer mTLS", source_unavailable: "Indisponível",
  access_denied: "Acesso negado", blocked: "Bloqueada (allowlist)", parse_error: "Erro ao processar",
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("pt-BR"); } catch { return iso; }
};

export default function MonitorFiscal() {
  const [summary, setSummary] = useState(null);
  const [sources, setSources] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [filters, setFilters] = useState({ severity: "", status: "", category: "" });
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [note, setNote] = useState("");

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.severity) params.severity = filters.severity;
      if (filters.status) params.status = filters.status;
      if (filters.category) params.category = filters.category;
      const [{ data: s }, { data: src }, { data: al }] = await Promise.all([
        api.get("/admin/regulatory/summary"),
        api.get("/admin/regulatory/sources"),
        api.get("/admin/regulatory/alerts", { params }),
      ]);
      setSummary(s); setSources(src); setAlerts(al);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const runScanNow = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/admin/regulatory/scan");
      toast.success(
        `Verificação concluída — ${data.fontes_verificadas} fonte(s), ${data.sem_alteracao} sem alteração, ` +
        `${data.alteradas} alterada(s), ${data.alertas_criados} alerta(s) novo(s), ${data.falhas} falha(s).`
      );
      await loadAll();
    } catch (e) {
      if (e?.response?.status === 409) toast.error("Já existe uma verificação em andamento — aguarde ela terminar.");
      else toast.error(formatApiError(e));
    } finally {
      setScanning(false);
    }
  };

  const toggleSource = async (source, active) => {
    try {
      await api.patch(`/admin/regulatory/sources/${source.id}`, { active });
      setSources(prev => prev.map(s => s.id === source.id ? { ...s, active } : s));
      toast.success(active ? "Fonte ativada." : "Fonte desativada.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openDetail = async (alertRow) => {
    setDetailLoading(true);
    setNote("");
    try {
      const { data } = await api.get(`/admin/regulatory/alerts/${alertRow.id}`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDetailLoading(false);
    }
  };

  const changeStatus = async (newStatus) => {
    if (!detail) return;
    try {
      const { data } = await api.post(`/admin/regulatory/alerts/${detail.id}/status`, { status: newStatus, note: note || undefined });
      setDetail(prev => ({ ...prev, ...data }));
      setNote("");
      toast.success("Status atualizado.");
      await loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const counts = summary?.counts || { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0, INFO: 0 };

  return (
    <div className="space-y-6" data-testid="monitor-fiscal-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2"><ShieldAlert size={22} /> Monitor Fiscal</h1>
          <p className="text-sm text-slate-500 mt-1">
            Monitora fontes oficiais da NFS-e Nacional e alerta sobre mudanças — nunca altera código, XSD
            ou a integração fiscal automaticamente. Última verificação: {fmtDateTime(summary?.last_checked_at)} ·
            Próxima verificação programada: {fmtDateTime(summary?.next_checked_at)}
          </p>
        </div>
        <Button onClick={runScanNow} disabled={scanning} data-testid="monitor-fiscal-scan-now">
          <RefreshCw size={16} className={scanning ? "animate-spin mr-2" : "mr-2"} />
          {scanning ? "Verificando..." : "Verificar agora"}
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {SEVERITIES.map(sev => (
          <Card key={sev} className="p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500">{SEVERITY_LABEL[sev]}</div>
            <div className="text-2xl font-bold mt-1">{counts[sev] ?? 0}</div>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="alertas">
        <TabsList>
          <TabsTrigger value="alertas" data-testid="monitor-fiscal-tab-alertas">Alertas</TabsTrigger>
          <TabsTrigger value="fontes" data-testid="monitor-fiscal-tab-fontes">Fontes monitoradas</TabsTrigger>
        </TabsList>

        <TabsContent value="alertas" className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Select value={filters.severity || "all"} onValueChange={(v) => setFilters(f => ({ ...f, severity: v === "all" ? "" : v }))}>
              <SelectTrigger className="w-44"><SelectValue placeholder="Severidade" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as severidades</SelectItem>
                {SEVERITIES.map(s => <SelectItem key={s} value={s}>{SEVERITY_LABEL[s]}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.status || "all"} onValueChange={(v) => setFilters(f => ({ ...f, status: v === "all" ? "" : v }))}>
              <SelectTrigger className="w-48"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os status</SelectItem>
                {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <Card className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left p-3">Nível</th>
                  <th className="text-left p-3">Assunto</th>
                  <th className="text-left p-3">Fonte</th>
                  <th className="text-left p-3">Detectado em</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={6} className="p-6 text-center text-slate-400">Carregando…</td></tr>
                )}
                {!loading && alerts.length === 0 && (
                  <tr><td colSpan={6} className="p-6 text-center text-slate-400">Nenhum alerta encontrado.</td></tr>
                )}
                {alerts.map(a => (
                  <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="p-3"><Badge className={SEVERITY_BADGE[a.severity]}>{SEVERITY_LABEL[a.severity] || a.severity}</Badge></td>
                    <td className="p-3 max-w-[320px] truncate" title={a.title}>{a.title}</td>
                    <td className="p-3 text-slate-600">{a.source_name}</td>
                    <td className="p-3 text-slate-600">{fmtDateTime(a.detected_at)}</td>
                    <td className="p-3">{STATUS_LABEL[a.status] || a.status}</td>
                    <td className="p-3">
                      <Button size="sm" variant="outline" onClick={() => openDetail(a)} data-testid={`monitor-fiscal-ver-${a.id}`}>Ver alteração</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="fontes" className="space-y-3">
          <Card className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left p-3">Fonte</th>
                  <th className="text-left p-3">Categoria</th>
                  <th className="text-left p-3">Última verificação</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Ativa</th>
                  <th className="text-left p-3">URL oficial</th>
                </tr>
              </thead>
              <tbody>
                {sources.map(s => (
                  <tr key={s.id} className="border-t border-slate-100">
                    <td className="p-3">{s.name}</td>
                    <td className="p-3 text-slate-600">{s.category}</td>
                    <td className="p-3 text-slate-600">{fmtDateTime(s.last_checked_at)}</td>
                    <td className="p-3 text-slate-600">{SOURCE_STATUS_LABEL[s.last_status] || s.last_status || "—"}</td>
                    <td className="p-3"><Switch checked={!!s.active} onCheckedChange={(v) => toggleSource(s, v)} /></td>
                    <td className="p-3">
                      <a href={s.url} target="_blank" rel="noreferrer" className="text-blue-600 inline-flex items-center gap-1 hover:underline">
                        Abrir <ExternalLink size={12} />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {detailLoading || !detail ? (
            <div className="p-6 text-center text-slate-400">Carregando…</div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Badge className={SEVERITY_BADGE[detail.severity]}>{SEVERITY_LABEL[detail.severity] || detail.severity}</Badge>
                  {detail.title}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-2 text-slate-600">
                  <div><b>Fonte:</b> {detail.source_name}</div>
                  <div><b>Status atual:</b> {STATUS_LABEL[detail.status] || detail.status}</div>
                  <div><b>Detectado em:</b> {fmtDateTime(detail.detected_at)}</div>
                  <div><b>Vigência detectada:</b> {detail.vigencia || "—"}</div>
                  <div><b>Versão anterior:</b> {detail.old_version || "—"}</div>
                  <div><b>Versão nova:</b> {detail.new_version || "—"}</div>
                </div>
                <div>
                  <div className="font-semibold mb-1">Resumo</div>
                  <p className="text-slate-700">{detail.summary}</p>
                </div>
                {detail.affected_components?.length > 0 && (
                  <div>
                    <div className="font-semibold mb-1">Componentes afetados</div>
                    <ul className="list-disc pl-5 text-slate-700">
                      {detail.affected_components.map((c, i) => <li key={i}>{c}</li>)}
                    </ul>
                  </div>
                )}
                {detail.evidence && (
                  <div>
                    <div className="font-semibold mb-1">Evidência</div>
                    <div className="text-xs bg-slate-50 rounded-md p-3 space-y-1 break-all">
                      <div><b>URL oficial:</b> <a href={detail.evidence.official_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">{detail.evidence.official_url}</a></div>
                      <div><b>Hash anterior:</b> {detail.evidence.old_hash}</div>
                      <div><b>Hash novo:</b> {detail.evidence.new_hash}</div>
                      {detail.evidence.diff_added && <div className="mt-1"><b>Adicionado:</b> <span className="text-emerald-700">{detail.evidence.diff_added}</span></div>}
                      {detail.evidence.diff_removed && <div><b>Removido:</b> <span className="text-red-700 line-through">{detail.evidence.diff_removed}</span></div>}
                    </div>
                  </div>
                )}
                <div>
                  <div className="font-semibold mb-1 flex items-center gap-1"><History size={14} /> Histórico de status</div>
                  <ul className="text-xs text-slate-600 space-y-1">
                    {(detail.status_history || []).map((h, i) => (
                      <li key={i}>{fmtDateTime(h.at)} — {STATUS_LABEL[h.status] || h.status}{h.note ? ` (${h.note})` : ""}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <Textarea placeholder="Observação (opcional) ao mudar o status…" value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
                </div>
              </div>
              <DialogFooter className="flex-wrap gap-2">
                {STATUS_OPTIONS.filter(s => s !== detail.status).map(s => (
                  <Button key={s} size="sm" variant="outline" onClick={() => changeStatus(s)} data-testid={`monitor-fiscal-status-${s}`}>
                    {STATUS_LABEL[s]}
                  </Button>
                ))}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
